import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import { useSettings } from "../hooks/useSettings";
import { useCommunityShortName } from "../hooks/useCommunityShortName";
import { createLead } from "../api/leadApi";
import { toast } from "react-hot-toast";

export const Contact = () => {
  const { pageHeader, isLoading } = usePageHeaderByKey("contact");
  const { settings } = useSettings();
  const communityRecordShortName = useCommunityShortName();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [subjectOtherReason, setSubjectOtherReason] = useState("");
  const [preferredTitleOtherReason, setPreferredTitleOtherReason] = useState("");
  const [howDidYouFindUsOtherReason, setHowDidYouFindUsOtherReason] = useState("");

  // Header settings has no shortName field, so the community record is the real source.
  const communityShortName =
    communityRecordShortName || settings?.shortName || settings?.clubName || "the community";
  const propertyAddress =
    new URLSearchParams(location.search).get("property")?.trim() || "";
  const requestInfoText = propertyAddress
    ? `Request Info/Showing for: ${propertyAddress}`
    : "";
  const propertyAddressParts = propertyAddress.split(", ");
  const propertyAddressDisplayLines =
    propertyAddressParts.length > 1
      ? [
          propertyAddressParts[0],
          propertyAddressParts.slice(1).join(", "),
        ]
      : propertyAddressParts;

  const subjects = [
    "I would like to get information on property(ies)",
    "I would like to see property(ies)",
    "I would like to sell my home",
    "I would like to buy a home",
    "I am looking for a rental",
    "I would like to rent my unit out",
    "I am looking for property manager for my rental",
    "I would like to be notified when a home is listed for sale",
    "I would like to be notified when a home is listed for rent",
    "I would like to be notified when a home is sold",
    "I would like to be notified when a home is rented",
    "I would like to receive community newsletters",
    "I have a correction for the information posted",
    `I have a suggestion for ${communityShortName} website`,
    `I have a complaint about ${communityShortName} website`,
    `I like ${communityShortName} website`,
    "I have questions, please call me",
    "Other",
  ];

  const locationOptions = [
    `I own a unit in ${communityShortName}`,
    `I am a renter in ${communityShortName}`,
    `I am renting out my unit in ${communityShortName}`,
    `I live in ${communityShortName}`,
    "I live in another state",
    "I live in another country",
  ];

  const findingOptions = [
    "Internet search",
    "A friend/relative recommended",
    "On an advertisement",
    "From a frig magnet",
    "From the HOA management office",
    "From a real estate agent",
    "Other",
  ];

  const [formData, setFormData] = useState({
    subject: [],
    firstName: "",
    lastName: "",
    nameSuffix: "",
    preferredTitle: "",
    cellPhone: "",
    phoneCountryCode: "",
    email: "",
    location: [],
    howDidYouFindUs: "",
    additionalInfo: requestInfoText,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "preferredTitle" && value !== "Other") {
      setPreferredTitleOtherReason("");
      if (errors.preferredTitleOtherReason) {
        setErrors((prev) => ({ ...prev, preferredTitleOtherReason: "" }));
      }
    }
    if (name === "howDidYouFindUs" && value !== "Other") {
      setHowDidYouFindUsOtherReason("");
      if (errors.howDidYouFindUsOtherReason) {
        setErrors((prev) => ({ ...prev, howDidYouFindUsOtherReason: "" }));
      }
    }
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData((prev) => {
      let arr = [...prev[field]];
      if (checked) {
        arr.push(value);
      } else {
        arr = arr.filter((v) => v !== value);
      }
      return { ...prev, [field]: arr };
    });
    if (field === "subject" && value === "Other" && !checked) {
      setSubjectOtherReason("");
    }
    setErrors((prev) => ({
      ...prev,
      [field]: "",
      ...(field === "subject" && value === "Other" && !checked
        ? { subjectOtherReason: "" }
        : {}),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (formData.subject.length === 0) newErrors.subject = "Select at least one subject";
    if (formData.subject.includes("Other") && !subjectOtherReason.trim()) {
      newErrors.subjectOtherReason = 'Please specify is required';
    }
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.preferredTitle) newErrors.preferredTitle = "Preferred title is required";
    if (formData.preferredTitle === "Other" && !preferredTitleOtherReason.trim()) {
      newErrors.preferredTitleOtherReason = 'Please specify is required';
    }
    if (!formData.cellPhone.trim()) newErrors.cellPhone = "Cell phone is required";
    if (!formData.phoneCountryCode.trim()) {
      newErrors.phoneCountryCode = "Phone number country name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (formData.location.length === 0) newErrors.location = "At least one location is required";
    if (!formData.howDidYouFindUs) newErrors.howDidYouFindUs = "Please select how you found us";
    if (formData.howDidYouFindUs === "Other" && !howDidYouFindUsOtherReason.trim()) {
      newErrors.howDidYouFindUsOtherReason = 'Please specify is required';
    }
    if (!formData.additionalInfo.trim()) newErrors.additionalInfo = "Additional info is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const subjectOther = subjectOtherReason.trim();
      const preferredTitleOther = preferredTitleOtherReason.trim();
      const howDidYouFindUsOther = howDidYouFindUsOtherReason.trim();
      const additionalInfo = propertyAddress
        ? [
            `Property address: ${propertyAddress}`,
            formData.additionalInfo.trim(),
          ]
            .filter(Boolean)
            .join("\n\n")
        : formData.additionalInfo;
      const payload = {
        ...formData,
        additionalInfo,
        subject: formData.subject.map((item) =>
          item === "Other" && subjectOther ? `Other: ${subjectOther}` : item
        ),
        subjectOtherReason: subjectOther,
        preferredTitleOtherReason: preferredTitleOther,
        howDidYouFindUsOtherReason: howDidYouFindUsOther,
        howDidYouFindUs:
          formData.howDidYouFindUs === "Other" && howDidYouFindUsOther
            ? `Other: ${howDidYouFindUsOther}`
            : formData.howDidYouFindUs,
        sourcePage: "Contact",
      };
      await createLead(payload);
      sessionStorage.setItem(
        "contactThankYou",
        JSON.stringify({
          preferredTitle: formData.preferredTitle,
          preferredTitleOtherReason: preferredTitleOther,
          lastName: formData.lastName,
        })
      );
      navigate("/thank-you");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading)
    return (
      <div className="flex h-screen justify-center items-center">
        <i className="pi pi-spin pi-spinner text-red-700" style={{ fontSize: "2rem" }}></i>
      </div>
    );

  const inputStyle = { fontFamily: "Tahoma, sans-serif" };
  const labelStyle = { fontFamily: "Tahoma, sans-serif", fontWeight: "bold" };
  const errorStyle = { color: "#dc2626", fontSize: "0.8rem", marginTop: "2px" };
  const requiredFormErrorStyle = {
    color: "#dc2626",
    fontSize: "0.95rem",
    fontWeight: 700,
    marginTop: "-8px",
    marginBottom: "8px",
  };

  const showRequiredFormError = Object.values(errors).some(
    (v) => v != null && String(v).trim() !== ""
  );

  return (
    <div style={{ fontFamily: "Tahoma, sans-serif", color: "black" }}>
      <div className="min-h-[30rem] w-full">
        <div className="w-full max-w-2xl">
          {/* Left-aligned so the heading lines up with the form fields below it. */}
          <h1
            className="text-2xl mb-4 uppercase text-left"
            style={{ fontFamily: "Tahoma, sans-serif", fontWeight: "bold" }}
          >
            {pageHeader?.h1 || "Contact Us"}
          </h1>
          <form onSubmit={handleSubmit}>
            <div className="py-6 w-full gap-4" style={inputStyle}>
              {propertyAddress && (
                <div
                  className="mb-6 text-left"
                  style={{
                    color: "#ed1c24",
                    fontFamily: "Tahoma, sans-serif",
                    fontWeight: 800,
                    fontSize: "1.125rem",
                    lineHeight: 1.45,
                  }}
                >
                  <div>Unit address:</div>
                  <div>
                    {propertyAddressDisplayLines.map((part, index) => (
                      <span key={`${part}-${index}`}>
                        {part}
                        {index < propertyAddressDisplayLines.length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="mb-4">
                <label className="block mb-2 font-medium" style={labelStyle}>
                  Subject (select all applicable): *
                </label>
                <div className="space-y-2" style={inputStyle}>
                  {subjects.map((subject, index) => (
                    <label key={index} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value={subject}
                        checked={formData.subject.includes(subject)}
                        onChange={(e) =>
                          handleCheckboxChange("subject", subject, e.target.checked)
                        }
                        className="form-checkbox text-blue-600"
                      />
                      <span>{subject}</span>
                    </label>
                  ))}
                </div>
                {errors.subject && <div style={errorStyle}>{errors.subject}</div>}
                {formData.subject.includes("Other") && (
                  <div className="mt-3">
                    <label className="block mb-2 font-medium" style={labelStyle}>
                      Please specify *
                    </label>
                    <input
                      type="text"
                      name="subjectOtherReason"
                      value={subjectOtherReason}
                      onChange={(e) => {
                        setSubjectOtherReason(e.target.value);
                        if (errors.subjectOtherReason) {
                          setErrors((prev) => ({ ...prev, subjectOtherReason: "" }));
                        }
                      }}
                      placeholder="Please explain the other subject"
                      className="w-full p-2 border rounded"
                      style={inputStyle}
                    />
                    {errors.subjectOtherReason && (
                      <div style={errorStyle}>{errors.subjectOtherReason}</div>
                    )}
                  </div>
                )}
              </div>

              {/* First Name */}
              <div className="mb-4">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name *"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  style={inputStyle}
                />
                {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
              </div>

              {/* Last Name */}
              <div className="mb-4">
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name *"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  style={inputStyle}
                />
                {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
              </div>

              {/* Preferred Title */}
              <div className="mb-4">
                <label className="block mb-2 font-medium" style={labelStyle}>
                  Preferred title: *
                </label>
                {["Mr.", "Mrs.", "Ms.", "Miss", "Other"].map((title) => (
                  <div key={title} className="flex items-center mb-1" style={inputStyle}>
                    <input
                      type="radio"
                      name="preferredTitle"
                      value={title}
                      checked={formData.preferredTitle === title}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>{title}</span>
                  </div>
                ))}
                {errors.preferredTitle && <div style={errorStyle}>{errors.preferredTitle}</div>}
                {formData.preferredTitle === "Other" && (
                  <div className="mt-3">
                    <label className="block mb-2 font-medium" style={labelStyle}>
                      Please specify *
                    </label>
                    <input
                      type="text"
                      name="preferredTitleOtherReason"
                      value={preferredTitleOtherReason}
                      onChange={(e) => {
                        setPreferredTitleOtherReason(e.target.value);
                        if (errors.preferredTitleOtherReason) {
                          setErrors((prev) => ({ ...prev, preferredTitleOtherReason: "" }));
                        }
                      }}
                      placeholder="Please explain the other preferred title"
                      className="w-full p-2 border rounded"
                      style={inputStyle}
                    />
                    {errors.preferredTitleOtherReason && (
                      <div style={errorStyle}>{errors.preferredTitleOtherReason}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Name Suffix */}
              <div className="mb-4">
                <label className="block mb-2 font-medium" style={labelStyle}>
                  Name suffix:
                </label>
                {["Jr", "Sr", "I", "II", "III", "IV", "V"].map((suffix) => (
                  <div key={suffix} className="flex items-center mb-1" style={inputStyle}>
                    <input
                      type="radio"
                      name="nameSuffix"
                      value={suffix}
                      checked={formData.nameSuffix === suffix}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>{suffix}</span>
                  </div>
                ))}
              </div>

              {/* Cell Phone */}
              <div className="mb-4">
                <input
                  type="text"
                  name="cellPhone"
                  placeholder="Cell phone *"
                  value={formData.cellPhone}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  style={inputStyle}
                />
                {errors.cellPhone && <div style={errorStyle}>{errors.cellPhone}</div>}
              </div>

              {/* Phone Number Country Name */}
              <div className="mb-4">
                <input
                  type="text"
                  name="phoneCountryCode"
                  placeholder="Phone number country name *"
                  value={formData.phoneCountryCode}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  style={inputStyle}
                />
                {errors.phoneCountryCode && <div style={errorStyle}>{errors.phoneCountryCode}</div>}
              </div>

              {/* Email */}
              <div className="mb-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  style={inputStyle}
                />
                {errors.email && <div style={errorStyle}>{errors.email}</div>}
              </div>

              {/* Location */}
              <div className="mb-4">
                <label className="block mb-2 font-medium" style={labelStyle}>
                  Location (select all that apply): *
                </label>
                <div className="space-y-2" style={inputStyle}>
                  {locationOptions.map((loc, index) => (
                    <label key={index} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value={loc}
                        checked={formData.location.includes(loc)}
                        onChange={(e) =>
                          handleCheckboxChange("location", loc, e.target.checked)
                        }
                        className="form-checkbox text-blue-600"
                      />
                      <span>{loc}</span>
                    </label>
                  ))}
                </div>
                {errors.location && <div style={errorStyle}>{errors.location}</div>}
              </div>

              {/* How Did You Find Us */}
              <div className="mb-4">
                <label className="block mb-2 font-medium" style={labelStyle}>
                  How did you find us (select one): *
                </label>
                {findingOptions.map((option) => (
                  <div key={option} className="flex items-center mb-1" style={inputStyle}>
                    <input
                      type="radio"
                      name="howDidYouFindUs"
                      value={option}
                      checked={formData.howDidYouFindUs === option}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>{option}</span>
                  </div>
                ))}
                {errors.howDidYouFindUs && <div style={errorStyle}>{errors.howDidYouFindUs}</div>}

                {formData.howDidYouFindUs === "Other" && (
                  <div className="mt-3">
                    <label className="block mb-2 font-medium" style={labelStyle}>
                      Please specify *
                    </label>
                    <input
                      type="text"
                      name="howDidYouFindUsOtherReason"
                      value={howDidYouFindUsOtherReason}
                      onChange={(e) => {
                        setHowDidYouFindUsOtherReason(e.target.value);
                        if (errors.howDidYouFindUsOtherReason) {
                          setErrors((prev) => ({ ...prev, howDidYouFindUsOtherReason: "" }));
                        }
                      }}
                      placeholder="Please tell us how you found us"
                      className="w-full p-2 border rounded"
                      style={inputStyle}
                    />
                    {errors.howDidYouFindUsOtherReason && (
                      <div style={errorStyle}>{errors.howDidYouFindUsOtherReason}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <textarea
                name="additionalInfo"
                placeholder="Additional information you would like to add *"
                value={formData.additionalInfo}
                onChange={handleChange}
                className="w-full p-2 border rounded mb-2"
                style={inputStyle}
                rows={4}
              />
              {errors.additionalInfo && <div style={errorStyle}>{errors.additionalInfo}</div>}

              {showRequiredFormError && (
                <div style={requiredFormErrorStyle}>
                  Please complete all required fields highlighted above.
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-[#BA1500] text-white py-2 rounded mt-4"
                disabled={submitting}
                style={inputStyle}
              >
                {submitting ? (
                  <i className="pi pi-spin pi-spinner text-white"></i>
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
